import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AppConstants } from '@core/constants';
import { ApiBaseService } from '@core/services/api-base.service';
import { IIntegrationLink } from '@shared/interfaces/integration-link.interface';
import { IIntegrationTemplate } from '@shared/interfaces/integration-template.interface';
import { String } from 'typescript-string-operations';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { IntegrationVQVComponentState } from './integration-vqv.component.state';
import { PlanActivatedDto } from '@core/services/plan-purchase/plan-dto';
import { BehaviorSubject } from 'rxjs';


@Component({
  selector: 'app-integration-vqv',
  templateUrl: './integration-vqv.component.html',
  styleUrls: ['./integration-vqv.component.scss'],
  providers: [IntegrationVQVComponentState],
})
export class IntegrationVqvComponent implements OnInit {
  @Input() public template!: IIntegrationTemplate | undefined;
  @Input() public integration: IIntegrationLink | undefined;

  public showIntegrationActiveFlag$ = new BehaviorSubject(false);
  public integrationForm!: FormGroup;
  public purchases: PlanActivatedDto[] = [];

  constructor(
    private fb: FormBuilder,
    private apiService: ApiBaseService,
    private snackBar: MatSnackBar,
    private state: IntegrationVQVComponentState,
  ) { }

  ngOnInit(): void {
    this.state.loadState();
    this.state.purchases$.subscribe(purchases => this.purchases = purchases);
    this.initForm();
    this.populateForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.populateForm();
  }

  private initForm() {
    this.integrationForm = this.fb.group({
      purchase: new FormControl<string | null>(null, [Validators.required]),
      is_active: new FormControl<boolean>(true),
    });
  }

  private populateForm() {
    if (!this.integration) {
      return
    }

    this.showIntegrationActiveFlag$.next(true);
    this.integrationForm.patchValue({
      is_active: this.integration.is_active,
      purchase: this.integration.configuration?.purchase,
    })
  }

  public canSave(): boolean {
    return this.integrationForm.valid;
  }

  public save() {
    const saveData: Record<string, any> = {
      configuration: {
        purchase: this.integrationForm.get("purchase")?.value,
      },
      is_active: this.integrationForm.get("is_active")?.value,
    };

    if (this.integration != null) {
      return this.update(this.integration.id, saveData);
    }

    saveData.template = this.template?.id;
    return this.create(saveData);
  }

  public getActiveIconClass(): string {
    if (!this.integration) {
      return "inactive"
    }

    return this.integration.is_active ? "active" : "inactive";
  }

  public getActiveIconText(): string {
    if (!this.integration) {
      return "Inactive"
    }

    return this.integration.is_active ? "Active" : "Inactive";
  }

  public getToggleActivationButtonText(): string {
    if (!this.integration) {
      // Doesn't matter - the button will be hidden anyway
      return "Activate"
    }

    return this.integration.is_active ? "Deactivate" : "Activate";
  }

  public getSaveButtonText(): string {
    if (!this.integration) {
      return "Activate"
    }

    return "Save"
  }

  public toggleActivation(): void {
    this.integrationForm.patchValue({
      is_active: !this.integrationForm.get("is_active")?.value
    });

    if (this.integration != null) {
      return this.update(this.integration.id, {
        configuration: {
          purchase: this.integrationForm.get("purchase")?.value,
        },
        is_active: this.integrationForm.get("is_active")?.value,
      });
    }
  }

  private update(id: number, data: Record<string, any>) {
    const apiUrl: string = String.Format(AppConstants.API.INTEGRATIONS.LINKS.UPDATE, id);
    this.apiService.patchJson<IIntegrationLink>(apiUrl, data).subscribe({
      next: (response) => {
        this.integration = response;
        this.snackBar.open("Velocity - Vision Quest Virtual integration updated successfully", 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
      },
      error: (err) => {
        this.snackBar.open(err, 'OK', AppConstants.TOAST_CONFIG.ERROR);
      }
    });
  }

  private create(data: Record<string, any>) {
    this.apiService.postJson<IIntegrationLink>(AppConstants.API.INTEGRATIONS.LINKS.CREATE, data).subscribe({
      next: (response) => {
        this.integration = response;
        this.showIntegrationActiveFlag$.next(true);
        this.snackBar.open("Velocity - Vision Quest Virtual integration created successfully", 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
      },
      error: (err) => {
        this.snackBar.open(err, 'OK', AppConstants.TOAST_CONFIG.ERROR);
      }
    });
  }
}
